import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CheckCircleRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import colors from 'src/theme/colors';
import {
  LcaDiagramColumns,
  ProductImpactOutSchema,
} from '../LCADiagram/Diagram/index.types';
import ImpactGridTable from '../ImpactGrid/ImpactGridTable';
import { ProductPassportData } from '../types';
import {
  PASSPORT_BLOCK_DEFINITIONS,
  PassportBrandingSettings,
  PassportLayoutBlock,
  PassportBlockVariant,
} from './config';
import { PanelBody, PanelHead, RowName } from './index.styles';
import PassportQrCode from './PassportQrCode';

interface ProductPassportRendererProps {
  product: ProductPassportData;
  blocks: PassportLayoutBlock[];
  passportUrl?: string;
  showPageTitle?: boolean;
  isPreview?: boolean;
  branding?: PassportBrandingSettings;
  forceMobileLayout?: boolean;
  impactsLoading?: boolean;
}

type SectionRow = {
  label: string;
  value: React.ReactNode;
};

type LifeCycleMetric = {
  label: string;
  value: React.ReactNode;
};

const hasValue = (value: string | number | null | undefined) =>
  !(value === null || value === undefined || value === '');

const normalizeImpactName = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export default function ProductPassportRenderer({
  product,
  blocks,
  passportUrl,
  showPageTitle = false,
  isPreview = false,
  branding,
  forceMobileLayout = false,
  impactsLoading = false,
}: ProductPassportRendererProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'productPassport' });
  const { t: tBase } = useTranslation('lca');
  const theme = useTheme();
  const isViewportMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const useMobileLayout = forceMobileLayout || isViewportMobile;
  const accentColor = branding?.accentColor || colors.lcaMainDark;
  const logoUrl = branding?.logoUrl;
  const displayValue = (value: string | number | null | undefined) =>
    hasValue(value) ? value : t('notProvided');
  const displayTextOverride = (value: string | null | undefined) =>
    typeof value === 'string' && value.trim().length > 0 ? value : null;
  const impactLoadingValue = (
    <Stack direction="row" alignItems="center" spacing={1}>
      <CircularProgress size={16} />
      <Typography component="span" color="text.secondary">
        {tBase('loading')}
      </Typography>
    </Stack>
  );
  const formatImpactValue = (impact: ProductImpactOutSchema) => {
    if (impact.total_emission === null || impact.total_emission === undefined) {
      return t('notProvided');
    }

    return `${impact.total_emission} ${impact.area.unit_name}`;
  };

  const getDominantStage = (impact: ProductImpactOutSchema) => {
    const stageKey = (
      Object.entries(impact.emission_per_stage) as [LcaDiagramColumns, number][]
    ).reduce<LcaDiagramColumns | null>(
      (currentMaxKey, [candidateKey, value]) => {
        if (currentMaxKey === null) {
          return candidateKey;
        }

        return value > impact.emission_per_stage[currentMaxKey]
          ? candidateKey
          : currentMaxKey;
      },
      null
    );

    if (!stageKey) return null;

    return product.diagram.stages.find((stage) => stage.key === stageKey)?.name;
  };

  const findImpactByAliases = (aliases: string[]) => {
    const normalizedAliases = aliases.map(normalizeImpactName);

    return product.impacts.find((impact) => {
      const normalizedName = normalizeImpactName(impact.area.name);
      return normalizedAliases.some(
        (alias) =>
          normalizedName === alias ||
          normalizedName.includes(alias) ||
          alias.includes(normalizedName)
      );
    });
  };

  const renderImpactValue = (aliases: string[]) => {
    const impact = findImpactByAliases(aliases);

    if (!impact) {
      if (impactsLoading) {
        return impactLoadingValue;
      }
      return t('notProvided');
    }

    const dominantStage = getDominantStage(impact);

    return (
      <Box>
        <Typography>{formatImpactValue(impact)}</Typography>
        {dominantStage && (
          <Typography variant="body2" color="text.secondary">
            {t('lifeCycleImpactDominantStage', { stage: dominantStage })}
          </Typography>
        )}
      </Box>
    );
  };

  const renderManualOrComputedValue = (
    manualValue: string | null | undefined,
    fallback: React.ReactNode
  ) => {
    const override = displayTextOverride(manualValue);
    return override || fallback;
  };

  const lifeCycleMetrics: LifeCycleMetric[] = [
    {
      label: t('lifeCycleMetrics.productCarbonFootprintGhgEmissions'),
      value: renderManualOrComputedValue(
        product.product_carbon_footprint,
        renderImpactValue([
          'climate change',
          'global warming potential',
          'carbon footprint',
          'product carbon footprint',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.emissionsAirWaterSoilNoise'),
      value: renderManualOrComputedValue(
        product.emissions_to_air_water_soil_noise,
        renderImpactValue([
          'emissions to air water or soil',
          'emissions to air water or soil at any life cycle stage including noise',
          'emissions to air',
          'emissions to water',
          'emissions to soil',
          'noise',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.releaseMicroplasticsNanoplastics'),
      value: renderManualOrComputedValue(
        product.microplastics_nanoplastics_release,
        renderImpactValue([
          'release of microplastics and nanoplastics',
          'microplastics',
          'nanoplastics',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.consumptionEnergyWaterResources'),
      value: renderManualOrComputedValue(
        product.energy_water_resource_consumption,
        renderImpactValue([
          'consumption of energy water and other resources',
          'water use',
          'resource use',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.primaryEnergyUse'),
      value: renderManualOrComputedValue(
        product.primary_energy_use,
        renderImpactValue([
          'renewable and non renewable primary energy use',
          'renewable primary energy use',
          'non renewable primary energy use',
          'primary energy use',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.recoveredExportedEnergy'),
      value: renderManualOrComputedValue(
        product.recovered_exported_energy,
        renderImpactValue([
          'energy recovered and exported energy',
          'exported energy',
          'recovered energy',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.productPerformanceUpdatesImpact'),
      value: renderManualOrComputedValue(
        product.product_performance_factors_impact,
        displayValue(product.actual_product_use_information)
      ),
    },
    {
      label: t('lifeCycleMetrics.deforestationImpact'),
      value: renderManualOrComputedValue(
        product.deforestation_impact,
        renderImpactValue(['impact on deforestation', 'deforestation'])
      ),
    },
    {
      label: t('lifeCycleMetrics.shareRecycledMaterials'),
      value: renderManualOrComputedValue(
        product.recycled_materials_share,
        product.proportion_recycled_materials !== null &&
          product.proportion_recycled_materials !== undefined
          ? `${product.proportion_recycled_materials}%`
          : t('notProvided')
      ),
    },
    {
      label: t('lifeCycleMetrics.materialRecovery'),
      value: renderManualOrComputedValue(
        product.material_recovery_critical_raw_materials,
        displayValue(product.recycling_possibilities)
      ),
    },
    {
      label: t('lifeCycleMetrics.renewableMaterials'),
      value: renderManualOrComputedValue(
        product.renewable_materials_content,
        renderImpactValue([
          'use or content of renewable materials from sustainable sources',
          'renewable materials',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.wasteGenerated'),
      value: renderManualOrComputedValue(
        product.waste_generated,
        renderImpactValue([
          'waste generated hazardous non hazardous radioactive',
          'hazardous waste',
          'non hazardous waste',
          'radioactive waste',
          'waste generated',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.co2SequestrationPotential'),
      value: renderManualOrComputedValue(
        product.co2_sequestration_potential,
        renderImpactValue([
          'co2 sequestration potential',
          'carbon sequestration potential',
        ])
      ),
    },
    {
      label: t('lifeCycleMetrics.ozoneDepletionPotential'),
      value: renderManualOrComputedValue(
        product.ozone_depletion_potential,
        renderImpactValue(['ozone depletion potential'])
      ),
    },
    {
      label: t('lifeCycleMetrics.acidificationPotential'),
      value: renderManualOrComputedValue(
        product.acidification_potential,
        renderImpactValue(['acidification potential'])
      ),
    },
    {
      label: t('lifeCycleMetrics.eutrophicationPotential'),
      value: renderManualOrComputedValue(
        product.eutrophication_potential,
        renderImpactValue(['eutrophication potential'])
      ),
    },
    {
      label: t('lifeCycleMetrics.photochemicalOzoneCreationPotential'),
      value: renderManualOrComputedValue(
        product.photochemical_ozone_creation_potential,
        renderImpactValue([
          'photochemical ozone creation potential',
          'photochemical ozone formation potential',
        ])
      ),
    },
  ];

  const renderRows = (
    rows: SectionRow[],
    variant: PassportBlockVariant | undefined
  ) => {
    if (useMobileLayout) {
      return (
        <Stack spacing={1.5} pt={2}>
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                p: 2,
                borderRadius: '18px',
                border: `1px solid ${alpha(accentColor, 0.14)}`,
                backgroundColor: alpha(accentColor, 0.04),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: colors.darkGrey,
                  fontWeight: 700,
                  mb: 0.75,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {row.label}
              </Typography>
              <Box
                component="div"
                sx={{
                  typography: 'body2',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {row.value}
              </Box>
            </Box>
          ))}
        </Stack>
      );
    }

    if (variant === 'cards') {
      return (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 2,
            pt: 2,
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                p: 2,
                borderRadius: '18px',
                border: `1px solid ${alpha(accentColor, 0.16)}`,
                backgroundColor: alpha(accentColor, 0.05),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: colors.darkGrey,
                  fontWeight: 700,
                  mb: 0.75,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {row.label}
              </Typography>
              <Typography variant="body1">{row.value}</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Table>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell width="30%">
                <RowName $accentColor={accentColor}>{row.label}</RowName>
              </TableCell>
              <TableCell>{row.value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderBlockShell = (
    block: PassportLayoutBlock,
    content: React.ReactNode,
    forceFullWidth = false
  ) => (
    <Box
      key={block.id}
      sx={{
        minWidth: 0,
        gridColumn: {
          xs: '1 / -1',
          md:
            useMobileLayout || forceFullWidth || block.width !== 'half'
              ? '1 / -1'
              : 'span 1',
        },
      }}
    >
      {content}
    </Box>
  );

  const renderSection = (
    block: PassportLayoutBlock,
    rows: SectionRow[],
    titleOverride?: string,
    bodyProps?: Record<string, unknown>
  ) => {
    const definition = PASSPORT_BLOCK_DEFINITIONS[block.type];

    return renderBlockShell(
      block,
      <>
        <PanelHead $accentColor={accentColor}>
          <Typography variant="h4">
            {titleOverride || block.titleOverride || t(definition.titleKey)}
          </Typography>
        </PanelHead>
        <PanelBody {...bodyProps}>{renderRows(rows, block.variant)}</PanelBody>
      </>
    );
  };

  const renderHero = (block: PassportLayoutBlock) => {
    const metadata = [
      product.manufacturer_name,
      product.production_country?.name,
      hasValue(product.weight) && product.weight_unit
        ? `${product.weight} ${product.weight_unit}`
        : null,
      product.gtin_code,
    ].filter((item): item is string => Boolean(item));

    return renderBlockShell(
      block,
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: useMobileLayout
            ? 'minmax(0, 1fr)'
            : { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.3fr) 320px' },
          gap: 3,
          mb: 4,
          p: { xs: 2, md: 3 },
          borderRadius: '28px',
          background: `linear-gradient(135deg, ${alpha(
            accentColor,
            0.05
          )} 0%, ${alpha(accentColor, 0.16)} 100%)`,
          border: `1px solid ${alpha(accentColor, 0.18)}`,
        }}
      >
        <Box>
          {logoUrl && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: 220,
                p: 1.25,
                mb: 2,
                borderRadius: '18px',
                backgroundColor: colors.pureWhite,
                border: `1px solid ${alpha(accentColor, 0.18)}`,
              }}
            >
              <img
                src={logoUrl}
                alt={tBase('product.sharePage.builder.logoAlt')}
                style={{
                  display: 'block',
                  width: '100%',
                  maxWidth: '180px',
                  maxHeight: '64px',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: accentColor,
              letterSpacing: '0.12em',
              mb: 1,
            }}
          >
            {block.titleOverride || t('hero')}
          </Typography>
          <Typography variant="h2" sx={{ mb: 1.5 }}>
            {product.name}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 720 }}
          >
            {displayValue(product.description)}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {metadata.map((item, index) => (
              <Chip
                key={`${item}-${index}`}
                label={item}
                sx={{
                  backgroundColor: colors.pureWhite,
                  border: `1px solid ${alpha(accentColor, 0.18)}`,
                }}
              />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            minHeight: 240,
            borderRadius: '24px',
            overflow: 'hidden',
            backgroundColor: colors.backgroundGrey,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Typography color="text.secondary">{t('notProvided')}</Typography>
          )}
        </Box>
      </Box>,
      true
    );
  };

  const renderMaterialsSection = (block: PassportLayoutBlock) => {
    const formatMaterialDetails = (percentage: number, recycledShare: number) =>
      tBase('productPassport.materialCompositionValue', {
        percentage: percentage.toFixed(0),
        recycled: recycledShare.toFixed(0),
      });

    if (useMobileLayout || block.variant === 'cards') {
      const cards = [
        {
          label: t('basicMaterial'),
          value: displayValue(product.basic_material),
        },
        ...product.materials.map((material) => ({
          label: material.name,
          value: formatMaterialDetails(
            material.percentage,
            material.recycled_share_pct
          ),
        })),
      ];

      return renderSection(
        block,
        cards.map((card) => ({ label: card.label, value: card.value }))
      );
    }

    return renderBlockShell(
      block,
      <>
        <PanelHead $accentColor={accentColor}>
          <Typography variant="h4">
            {block.titleOverride || t('materialComposition')}
          </Typography>
        </PanelHead>
        <PanelBody>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell width="30%">
                  <RowName $accentColor={accentColor}>
                    {t('basicMaterial')}
                  </RowName>
                </TableCell>
                <TableCell>{displayValue(product.basic_material)}</TableCell>
              </TableRow>
              {product.materials.length > 0 ? (
                product.materials.map((component) => (
                  <TableRow key={`${component.name}-${component.percentage}`}>
                    <TableCell>
                      <RowName $accentColor={accentColor}>
                        {component.name}
                      </RowName>
                    </TableCell>
                    <TableCell>
                      {formatMaterialDetails(
                        component.percentage,
                        component.recycled_share_pct
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2}>{t('notProvided')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </PanelBody>
      </>
    );
  };

  const renderCertificatesSection = (block: PassportLayoutBlock) => {
    const rows =
      product.certificates.length > 0
        ? product.certificates.map((certificate) => ({
            label: certificate.name,
            value: certificate.description || t('notProvided'),
          }))
        : [{ label: t('certifications'), value: t('notProvided') }];

    if (useMobileLayout || block.variant === 'cards') {
      return renderBlockShell(
        block,
        <>
          <PanelHead $accentColor={accentColor}>
            <Typography variant="h4">
              {block.titleOverride || t('certifications')}
            </Typography>
          </PanelHead>
          <PanelBody>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: useMobileLayout
                  ? 'minmax(0, 1fr)'
                  : {
                      xs: 'minmax(0, 1fr)',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                gap: 2,
                pt: 2,
              }}
            >
              {rows.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    p: 2,
                    borderRadius: '18px',
                    border: `1px solid ${alpha(accentColor, 0.16)}`,
                    backgroundColor: colors.pureWhite,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CheckCircleRounded color="success" />
                    <Typography fontWeight={700}>{row.label}</Typography>
                  </Box>
                  <Typography color="text.secondary">{row.value}</Typography>
                </Box>
              ))}
            </Box>
          </PanelBody>
        </>
      );
    }

    return renderBlockShell(
      block,
      <>
        <PanelHead $accentColor={accentColor}>
          <Typography variant="h4">
            {block.titleOverride || t('certifications')}
          </Typography>
        </PanelHead>
        <PanelBody>
          <Table>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell width="30%">
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="row"
                      gap="8px"
                    >
                      <CheckCircleRounded color="success" />
                      <RowName $accentColor={accentColor}>{row.label}</RowName>
                    </Box>
                  </TableCell>
                  <TableCell>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </PanelBody>
      </>
    );
  };

  const renderQrSection = (block: PassportLayoutBlock) => {
    if (!passportUrl) return null;

    // An uploaded code replaces the generated one, so the printed link follows
    // its declared target - otherwise the link and the code would contradict.
    const shareUrl =
      (product.passport_qr_image_url && product.passport_qr_target_url) ||
      passportUrl;

    return renderBlockShell(
      block,
      <>
        <PanelHead $accentColor={accentColor}>
          <Typography variant="h4">
            {block.titleOverride || t('qrShare')}
          </Typography>
        </PanelHead>
        <PanelBody>
          <Box
            sx={{
              pt: 2,
              display: 'grid',
              gridTemplateColumns: useMobileLayout
                ? 'minmax(0, 1fr)'
                : { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 220px' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {tBase('product.sharePage.passportLink')}
              </Typography>
              <Link
                href={shareUrl}
                underline="hover"
                sx={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
              >
                {shareUrl}
              </Link>
              {isPreview && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {tBase('product.sharePage.builder.publicDraftNotice')}
                </Alert>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1.5,
                borderRadius: '18px',
                border: `1px solid ${alpha(accentColor, 0.16)}`,
                backgroundColor: colors.pureWhite,
              }}
            >
              <PassportQrCode
                passportUrl={passportUrl}
                size={180}
                imageUrl={product.passport_qr_image_url}
                gtinCode={product.gtin_code}
              />
            </Box>
          </Box>
        </PanelBody>
      </>
    );
  };

  const renderAboutDppLcaSection = (block: PassportLayoutBlock) =>
    renderBlockShell(
      block,
      <>
        <PanelHead $accentColor={accentColor}>
          <Typography variant="h4">
            {block.titleOverride || t('aboutDppLca')}
          </Typography>
        </PanelHead>
        <PanelBody>
          <Typography sx={{ whiteSpace: 'pre-wrap', pt: 2 }}>
            {block.content ??
              tBase('product.sharePage.builder.defaultDppLcaText')}
          </Typography>
        </PanelBody>
      </>
    );

  const renderLifeCycleImpactDetails = (block: PassportLayoutBlock) =>
    renderSection(
      block,
      lifeCycleMetrics.map((metric) => ({
        label: metric.label,
        value: metric.value,
      })),
      undefined,
      { overflow: 'auto' }
    );

  const renderBlock = (block: PassportLayoutBlock) => {
    if (!block.enabled) return null;

    switch (block.type) {
      case 'hero':
        return renderHero(block);
      case 'about-dpp-lca':
        return renderAboutDppLcaSection(block);
      case 'product-identification':
        return renderSection(block, [
          { label: t('productName'), value: displayValue(product.name) },
          {
            label: t('manufacturerName'),
            value: displayValue(product.manufacturer_name),
          },
          {
            label: t('importerName'),
            value: displayValue(product.importer_name),
          },
          {
            label: t('modelBatchSerialNumber'),
            value: displayValue(product.model_batch_serial_number),
          },
          { label: t('gtin'), value: displayValue(product.gtin_code) },
          {
            label: t('countryOfManufacture'),
            value: displayValue(product.production_country?.name),
          },
        ]);
      case 'environmental-footprint':
        return renderSection(block, [
          {
            label: t('weight'),
            value:
              hasValue(product.weight) && product.weight_unit
                ? `${product.weight} ${product.weight_unit}`
                : t('notProvided'),
          },
          {
            label: t('dataCollectionPeriod'),
            value: displayValue(product.data_collection_period),
          },
          {
            label: t('proportionRecycledMaterials'),
            value: hasValue(product.proportion_recycled_materials)
              ? `${product.proportion_recycled_materials}%`
              : t('notProvided'),
          },
        ]);
      case 'material-composition':
        return renderMaterialsSection(block);
      case 'durability-repairability':
        return renderSection(block, [
          {
            label: t('expectedLifespan'),
            value: displayValue(
              product.expected_product_lifetime_years ||
                product.reference_service_life
            ),
          },
          {
            label: t('repairabilityInformation'),
            value: displayValue(product.repairability_information),
          },
          {
            label: t('availabilityOfSpareParts'),
            value: displayValue(product.availability_of_spare_parts),
          },
          {
            label: t('compatibilityWithCommonToolsAndSpareParts'),
            value: displayValue(
              product.compatibility_with_commonly_available_tools_and_spare_parts
            ),
          },
          {
            label: t('repairDisassemblyInstructions'),
            value: displayValue(
              product.repair_disassembly_instructions_description
            ),
          },
          {
            label: t('properUseInstructions'),
            value: displayValue(product.proper_use_instructions),
          },
        ]);
      case 'recycling-end-of-life':
        return renderSection(block, [
          {
            label: t('recyclability'),
            value: displayValue(product.recycling_possibilities),
          },
          {
            label: t('disassemblyInstructions'),
            value: displayValue(product.disassembly_instructions),
          },
          {
            label: t('eprInformation'),
            value: displayValue(product.epr_information_description),
          },
        ]);
      case 'regulatory-compliance':
        return renderSection(block, [
          {
            label: t('declarationOfConformity'),
            value: product.ce_mark ? t('ceApplicable') : t('ceNotApplicable'),
          },
        ]);
      case 'product-durability-reliability':
        return renderSection(block, [
          {
            label: t('guaranteedServiceLife'),
            value: displayValue(product.guaranteed_service_life),
          },
          {
            label: t('operatingLife'),
            value: displayValue(product.operating_life),
          },
          {
            label: t('meanTimeBetweenFailures'),
            value: displayValue(product.mean_time_between_failures),
          },
          {
            label: t('actualProductUseInformation'),
            value: displayValue(product.actual_product_use_information),
          },
          {
            label: t('resistanceToStressOrAgingMechanisms'),
            value: displayValue(
              product.resistance_to_stress_or_aging_mechanisms
            ),
          },
        ]);
      case 'certifications':
        return renderCertificatesSection(block);
      case 'carbon-footprint':
        return renderBlockShell(
          block,
          <>
            <PanelHead $accentColor={accentColor}>
              <Typography variant="h4">
                {block.titleOverride || t('carbonFootprint')}
              </Typography>
            </PanelHead>
            <PanelBody overflow="auto">
              {impactsLoading ? (
                <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography color="text.secondary">
                    {tBase('loading')}
                  </Typography>
                </Stack>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    '& table': {
                      minWidth: useMobileLayout ? 820 : 0,
                    },
                  }}
                >
                  <ImpactGridTable
                    diagramData={product.diagram}
                    impactData={product.impacts}
                  />
                </Box>
              )}
            </PanelBody>
          </>,
          true
        );
      case 'life-cycle-impact-details':
        return renderLifeCycleImpactDetails(block);
      case 'qr-share':
        return renderQrSection(block);
      case 'custom-text':
        return renderBlockShell(
          block,
          <>
            <PanelHead $accentColor={accentColor}>
              <Typography variant="h4">
                {block.titleOverride || t('customText')}
              </Typography>
            </PanelHead>
            <PanelBody>
              <Typography sx={{ whiteSpace: 'pre-wrap', pt: 2 }}>
                {block.content || tBase('product.sharePage.builder.emptyText')}
              </Typography>
            </PanelBody>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      {showPageTitle && (
        <Typography variant="h5" mt="16px" mb="24px" color={colors.darkGrey}>
          {t('title')}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: useMobileLayout
            ? 'minmax(0, 1fr)'
            : { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {blocks.filter((block) => block.enabled).map(renderBlock)}
      </Box>
    </Box>
  );
}
